import { useContext } from 'react';
import { css } from '@emotion/react';
import { isEmpty } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Avatar, Text, ShieldIcon } from '@frontend/design-system';
import { InsuranceReportContext } from '../dental-insurance-eligibility-report/onederful-insurance-eligibility-report/context/OnederfulReportContext.provider';
import {
  whiteRoundedBg,
  alignVertical,
  alignHorizontal,
  infoTitle,
  sectionContent,
  sectionTitle,
  alignItemsCenter,
  flexRowWrapItems,
} from './common-styles';

export const Profile = () => {
  const { profileData, personImage } = useContext(InsuranceReportContext);
  const { t } = useTranslation('insuranceEligibilityReport');
  if (!isEmpty(profileData)) {
    return (
      <div css={[whiteRoundedBg, containerStyle]}>
        <Avatar
          name={`${profileData?.firstName ?? ''} ${profileData?.lastName ?? ''}`.trim() || ''}
          src={personImage}
          size='xl'
        />
        <span css={[alignHorizontal, marginTop8, alignItemsCenter, flexRowWrapItems]}>
          {(profileData?.firstName || profileData?.lastName) && (
            <Text css={nameStyle}>{`${profileData.firstName ?? ''} ${profileData.lastName ?? ''}`.trim()}</Text>
          )}
          <Text css={[greenBadge, alignHorizontal, alignItemsCenter]}>
            <ShieldIcon /> {titleCase(profileData?.coverage?.status || t('Active'))}
          </Text>
        </span>
        <Text css={[sectionTitle, marginTop8]}>{t('Patient Details')}</Text>

        <section
          css={[
            alignHorizontal,
            marginTop12,
            css`
              align-items: flex-end;
            `,
          ]}
        >
          {profileData?.dateOfBirth && (
            <span css={alignVertical}>
              <Text css={sectionContent}>{profileData?.dateOfBirth}</Text>
              <Text css={infoTitle}>{t('Date of Birth')}</Text>
            </span>
          )}
          {(profileData?.addressLine1 || profileData?.addressLine2) && (
            <span css={[alignVertical, addressContainer]}>
              <Text css={address}>{profileData.addressLine1}</Text>
              <Text css={address}>{profileData?.addressLine2}</Text>
              <Text css={infoTitle}>{t('Address')}</Text>
            </span>
          )}
        </section>
        <Text css={[sectionTitle, marginTop24]}>{t('Plan Details')}</Text>
        <ul css={[alignHorizontal, marginTop12, planDetailsList]}>
          {profileData?.groupNumber && (
            <li css={alignVertical}>
              <Text css={sectionContent}>{profileData?.groupNumber}</Text>
              <Text css={infoTitle}>{t('Group Number')}</Text>
            </li>
          )}
          {profileData?.planName && (
            <li css={alignVertical}>
              <Text css={sectionContent}>{profileData?.planName}</Text>
              <Text css={infoTitle}>{t('Name')}</Text>
            </li>
          )}
          {profileData?.state && (
            <li css={alignVertical}>
              <Text css={sectionContent}>{profileData.state}</Text>
              <Text css={infoTitle}>{t('State')}</Text>
            </li>
          )}
          {profileData?.type && (
            <li css={alignVertical}>
              <Text css={sectionContent}>{profileData.type}</Text>
              <Text css={infoTitle}>{t('Type')}</Text>
            </li>
          )}
          {profileData?.payerId && (
            <li css={alignVertical}>
              <Text css={sectionContent}>{profileData.payerId}</Text>
              <Text css={infoTitle}>{t('Payer ID')}</Text>
            </li>
          )}
          {profileData?.payerName && (
            <li css={alignVertical}>
              <Text css={sectionContent}>{profileData.payerName}</Text>
              <Text css={infoTitle}>{t('Payer Name')}</Text>
            </li>
          )}
        </ul>
      </div>
    );
  }
  return null;
};

const containerStyle = css`
  grid-row: 1/3;
  @media (max-width: 900px) {
    grid-column: 1/3;
  }
`;

const nameStyle = css`
  font-size: ${theme.fontSize(24)};
  font-weight: ${theme.font.weight.bold};
`;

const addressContainer = css`
  margin: ${theme.spacing(0, 0, 0, 3)};
`;

const address = css`
  font-size: ${theme.fontSize(12)};
  color: ${theme.colors.neutral90};
`;

const marginTop8 = css`
  margin: ${theme.spacing(1, 0, 0, 0)};
`;

const marginTop12 = css`
  margin: ${theme.spacing(1.5, 0, 0, 0)};
`;

const marginTop24 = css`
  margin: ${theme.spacing(3, 0, 0, 0)};
`;

const greenBadge = css`
  border-radius: ${theme.borderRadius.small};
  background-color: ${theme.colors.success10};
  color: ${theme.colors.success50};
  font-weight: ${theme.font.weight.semibold};
  font-size: ${theme.fontSize(14)};
  padding: ${theme.spacing(0.25, 0.5)};
  svg {
    width: ${theme.spacing(2)};
    height: ${theme.spacing(2)};
  }
`;

const planDetailsList = css`
  justify-content: space-between;
  list-style: none;
  flex-flow: row wrap;
  padding: 0;
`;
